import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionName } from "../../../shared/types/permission";
import { userGroupService } from "../shared/api/userGroupService";
import { UserGroupFormState, UserGroupIndexResource, UserGroupRequest } from "../shared/types/userGroup";
import { UserGroupCreateDrawer, UserGroupUpdateDrawer } from "./UserGroupDrawer";

function mapToRequest(values: UserGroupFormState): UserGroupRequest {
	if (!values.name) throw "Missing validation for field: name";
	return {
		name: values.name,
		userIds: values.users.map((user) => user.id),
	};
}

export const UserGroupList: FC = () => {
	const { t } = useTranslation();
	const crudOperations = useLaravelCrudEntity(userGroupService, {
		entityName: t("User group"),
		messages: {
			create: t("User group was created."),
			update: t("User group was updated."),
			delete: t("User group was deleted."),
		},
	});
	const canCreate = useHasPermission<PermissionName>(["userGroup.create"]);
	const canUpdate = useHasPermission<PermissionName>(["userGroup.update"]);
	const dataListColumns: IDrawerDataListColumn<UserGroupIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Users count"),
				valueKey: "usersCount",
				type: "value",
			},
			{
				size: "max-content",
				type: "actions",
				heading: t("Actions"),
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit user group")}
							label={t("Edit user group")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete user group")}
							label={t("Delete user group")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this user group?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[t, crudOperations],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(userGroupService.listKey, userGroupService.index)}
			renderCreateDrawer={(drawerProps) => (
				<UserGroupCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(mapToRequest(entityFormState))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<UserGroupUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, mapToRequest(entityFormState))}
				/>
			)}
			translations={{
				title: t("User groups"),
				empty: t("No user group available."),
			}}
		/>
	);
};
